export const educationApiMethods = [
  'getEducationConfig',
  'getEducationRoutes',
  'getEducationFilters',
  'getEducationSortOptions',
  'getEducationColumns',
  'getEducationKnowledgeItems',
  'getEducationKnowledgeItemDetails',
  'getEducationReport',
  'getEducationQuestionBankItems',
  'listTags',
  'getLessonSections',
  'listLessonSectionItems',
  'getLessons',
  'getQuizCaseOptions',
  'getQuizCases',
  'getQuizGames',
  'getQuizGameContent',
  'createLesson',
  'updateLesson',
  'publishLesson',
  'listLessonMaterials',
  'createLessonTextMaterial',
  'updateLessonMaterial',
  'createLessonLinkMaterial',
  'createLessonFileMaterial',
  'uploadLessonFileMaterial',
  'uploadSmoozieFile',
  'downloadLessonMaterialContent',
  'createLessonDividerMaterial',
  'detachLessonMaterial',
  'setLessonMaterialsOrder',
  'reorderLessonSectionItems',
  'attachLessonSectionItem',
  'updateLessonSectionItem',
  'createLessonSection',
  'updateLessonSection',
  'createQuizCaseOption',
  'createQuizCase',
  'createQuizGame',
  'activateQuizGame',
  'deactivateQuizGame',
  'updateQuizGame',
  'updateQuizCase',
  'updateQuizCaseOption',
  'getEducationAchievements',
];

export const educationApiErrorShape = {
  code: 'string',
  details: 'unknown?',
  message: 'string',
  status: 'number?',
};

const educationAuthContextShape = {
  locale: 'string?',
  merchantId: 'string?',
  token: 'string?',
  userId: 'string?',
};

const contextPayloadShape = {
  context: 'EducationAuthContext',
};

const endpointDefinitions = {
  getEducationConfig: {
    path: '/config',
    responseShape: {
      type: 'EducationConfig',
    },
  },
  getEducationRoutes: {
    path: '/routes',
    responseShape: {
      item: 'EducationRoute',
      type: 'array',
    },
  },
  getEducationFilters: {
    path: '/filters',
    responseShape: {
      item: 'EducationFilter',
      type: 'array',
    },
  },
  getEducationSortOptions: {
    path: '/sort-options',
    responseShape: {
      item: 'EducationSortOption',
      type: 'array',
    },
  },
  getEducationColumns: {
    path: '/columns',
    responseShape: {
      item: 'EducationColumn',
      type: 'array',
    },
  },
  getEducationKnowledgeItems: {
    path: '/knowledge-items',
    responseShape: {
      item: 'EducationKnowledgeItemSummary',
      type: 'array',
    },
  },
  getEducationKnowledgeItemDetails: {
    buildPath: ({ itemId }) => `/knowledge-items/${encodeURIComponent(itemId)}`,
    payloadShape: {
      context: 'EducationAuthContext',
      itemId: 'string',
    },
    responseShape: {
      nullable: true,
      type: 'EducationKnowledgeItemDetails',
    },
  },
  getEducationReport: {
    path: '/report',
    responseShape: {
      type: 'EducationReport',
    },
  },
  getEducationQuestionBankItems: {
    path: '/question-bank',
    responseShape: {
      item: 'EducationQuestionBankItem',
      type: 'array',
    },
  },
  listTags: {
    buildData: (payload = {}) => pickDefinedPayloadFields({ cursor: payload.cursor }),
    method: 'POST',
    path: '/tags/list',
    responseShape: {
      item: 'EducationTag',
      type: 'array',
    },
  },
  getLessonSections: {
    buildData: (payload) => pickDefinedPayloadFields({
      status: payload.status,
      limit: payload.limit,
      cursor: payload.cursor,
    }),
    method: 'POST',
    path: '/lesson-sections/list',
    responseShape: {
      item: 'LessonSection',
      type: 'array',
    },
  },
  listLessonSectionItems: {
    buildData: (payload) => pickDefinedPayloadFields({
      sectionId: payload.sectionId,
      cursor: payload.cursor,
    }),
    method: 'POST',
    path: '/lesson-section-items/list',
    responseShape: {
      item: 'LessonSectionItem',
      type: 'array',
    },
  },
  getLessons: {
    buildData: (payload) => pickDefinedPayloadFields({
      status: payload.status,
      groupName: payload.groupName,
      sectionId: payload.sectionId,
      limit: payload.limit,
      cursor: payload.cursor,
    }),
    method: 'POST',
    path: '/lessons/list',
    responseShape: {
      item: 'Lesson',
      type: 'array',
    },
  },
  getQuizCaseOptions: {
    buildData: (payload = {}) => pickDefinedPayloadFields({ cursor: payload.cursor }),
    method: 'POST',
    path: '/quiz-case-options/list',
    responseShape: {
      item: 'QuizCaseOption',
      type: 'array',
    },
  },
  getQuizCases: {
    buildData: (payload = {}) => pickDefinedPayloadFields({
      cursor: payload.cursor,
    }),
    method: 'POST',
    path: '/quiz-cases/list',
    responseShape: {
      item: 'QuizCase',
      type: 'array',
    },
  },
  getQuizGames: {
    buildData: (payload = {}) => pickDefinedPayloadFields({
      status: payload.status,
      cursor: payload.cursor,
    }),
    method: 'POST',
    path: '/quiz-games/list',
    responseShape: {
      item: 'QuizGame',
      type: 'array',
    },
  },
  getQuizGameContent: {
    buildData: (payload = {}) => pickDefinedPayloadFields({
      quizGameId: payload.quizGameId,
    }),
    method: 'POST',
    path: '/quiz-games/content/get',
    responseShape: {
      type: 'QuizGameContent',
    },
  },
  createLesson: {
    buildData: (payload) => pickDefinedPayloadFields({
      groupName: payload.groupName,
      sectionId: payload.sectionId,
      title: payload.title,
      description: payload.description,
      estimatedDurationSeconds: payload.estimatedDurationSeconds,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/lessons/create',
    responseShape: {
      type: 'Lesson',
    },
  },
  updateLesson: {
    buildData: (payload) => pickDefinedPayloadFields({
      id: payload.id,
      groupName: payload.groupName,
      sectionId: payload.sectionId,
      title: payload.title,
      description: payload.description,
      estimatedDurationSeconds: payload.estimatedDurationSeconds,
      metadata: sanitizeMetadata(payload.metadata),
      tags: payload.tags,
      status: payload.status,
    }),
    method: 'POST',
    path: '/lessons/update',
    responseShape: {
      type: 'Lesson',
    },
  },
  publishLesson: {
    buildData: (payload) => pickDefinedPayloadFields({
      id: payload.id,
    }),
    method: 'POST',
    path: '/lessons/publish',
    responseShape: {
      type: 'Lesson',
    },
  },
  listLessonMaterials: {
    buildData: (payload) => pickDefinedPayloadFields({
      lessonId: payload.lessonId,
      cursor: payload.cursor,
    }),
    method: 'POST',
    path: '/lesson-materials/list',
    responseShape: {
      item: 'LessonMaterial',
      type: 'array',
    },
  },
  createLessonTextMaterial: {
    buildData: (payload) => pickDefinedPayloadFields({
      lessonId: payload.lessonId,
      title: payload.title,
      sortOrder: payload.sortOrder,
      text: payload.text,
      textFormat: payload.textFormat,
      description: payload.description,
      isRequired: payload.isRequired,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/lesson-materials/text/create',
    responseShape: {
      type: 'LessonMaterial',
    },
  },
  updateLessonMaterial: {
    buildData: (payload) => pickDefinedPayloadFields({
      lessonId: payload.lessonId,
      materialId: payload.materialId,
      title: payload.title,
      sortOrder: payload.sortOrder,
      text: payload.text,
      textFormat: payload.textFormat,
      description: payload.description,
      isRequired: payload.isRequired,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/lesson-materials/update',
    responseShape: {
      type: 'LessonMaterial',
    },
  },
  createLessonLinkMaterial: {
    buildData: (payload) => pickDefinedPayloadFields({
      lessonId: payload.lessonId,
      title: payload.title,
      href: payload.href,
      sortOrder: payload.sortOrder,
      description: payload.description,
      isRequired: payload.isRequired,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/lesson-materials/link/create',
    responseShape: {
      type: 'LessonMaterial',
    },
  },
  createLessonFileMaterial: {
    buildData: (payload) => pickDefinedPayloadFields({
      lessonId: payload.lessonId,
      title: payload.title,
      sortOrder: payload.sortOrder,
      fileName: payload.fileName,
      contentBase64: payload.contentBase64,
      description: payload.description,
      isRequired: payload.isRequired,
      mimeType: payload.mimeType,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/lesson-materials/file/create',
    responseShape: {
      type: 'LessonMaterial',
    },
  },
  uploadLessonFileMaterial: {
    buildData: (payload) => {
      if (payload.formData) return payload.formData;

      const formData = new FormData();
      const fields = pickDefinedPayloadFields({
        lessonId: payload.lessonId,
        title: payload.title,
        sortOrder: payload.sortOrder,
        fileName: payload.fileName,
        isRequired: payload.isRequired,
        mimeType: payload.mimeType,
      });

      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      if (payload.file) {
        formData.append('file', payload.file);
      }

      if (payload.metadata !== undefined) {
        formData.append('metadata', JSON.stringify(sanitizeMetadata(payload.metadata)));
      }

      return formData;
    },
    method: 'POST',
    path: '/lesson-materials/file/upload',
    responseShape: {
      type: 'LessonMaterial',
    },
  },
  uploadSmoozieFile: {
    buildData: (payload) => {
      if (payload.formData) return payload.formData;

      const formData = new FormData();
      const fields = pickDefinedPayloadFields({
        fileName: payload.fileName,
      });

      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      if (payload.file) {
        formData.append('file', payload.file);
      }

      return formData;
    },
    method: 'POST',
    path: '/files/upload',
    responseShape: {
      type: 'SmoozieFileUpload',
    },
  },
  downloadLessonMaterialContent: {
    buildData: (payload) => pickDefinedPayloadFields({
      lessonId: payload.lessonId,
      materialId: payload.materialId,
    }),
    method: 'POST',
    path: '/lesson-materials/content/download',
    responseShape: {
      type: 'LessonMaterialContent',
    },
  },
  createLessonDividerMaterial: {
    buildData: (payload) => pickDefinedPayloadFields({
      lessonId: payload.lessonId,
      title: payload.title,
      sortOrder: payload.sortOrder,
      description: payload.description,
      isRequired: payload.isRequired,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/lesson-materials/divider/create',
    responseShape: {
      type: 'LessonMaterial',
    },
  },
  detachLessonMaterial: {
    buildData: (payload) => pickDefinedPayloadFields({
      lessonId: payload.lessonId,
      materialId: payload.materialId,
    }),
    method: 'POST',
    path: '/lesson-materials/detach',
    responseShape: {
      type: 'object',
    },
  },
  setLessonMaterialsOrder: {
    buildData: (payload) => pickDefinedPayloadFields({
      lessonId: payload.lessonId,
      materialIds: payload.materialIds,
    }),
    method: 'POST',
    path: '/lesson-materials/order/set',
    responseShape: {
      type: 'object',
    },
  },
  reorderLessonSectionItems: {
    buildData: (payload) => pickDefinedPayloadFields({
      sectionId: payload.sectionId,
      items: payload.items,
    }),
    method: 'POST',
    path: '/lesson-section-items/reorder',
    responseShape: {
      type: 'object',
    },
  },
  attachLessonSectionItem: {
    buildData: (payload) => pickDefinedPayloadFields({
      sectionId: payload.sectionId,
      itemType: payload.itemType,
      itemId: payload.itemId,
      sortOrder: payload.sortOrder,
      canSkip: payload.canSkip,
    }),
    method: 'POST',
    path: '/lesson-section-items/attach',
    responseShape: {
      type: 'object',
    },
  },
  updateLessonSectionItem: {
    buildData: (payload) => pickDefinedPayloadFields({
      sectionId: payload.sectionId,
      sectionItemId: payload.sectionItemId,
      sortOrder: payload.sortOrder,
      canSkip: payload.canSkip,
    }),
    method: 'POST',
    path: '/lesson-section-items/update',
    responseShape: {
      type: 'object',
    },
  },
  createLessonSection: {
    buildData: (payload) => pickDefinedPayloadFields({
      title: payload.title,
      description: payload.description,
      sortOrder: payload.sortOrder,
      metadata: sanitizeLessonSectionMetadata(payload.metadata),
      tags: payload.tags,
    }),
    method: 'POST',
    path: '/lesson-sections/create',
    responseShape: {
      type: 'LessonSection',
    },
  },
  updateLessonSection: {
    buildData: (payload) => pickDefinedPayloadFields({
      id: payload.id,
      title: payload.title,
      description: payload.description,
      sortOrder: payload.sortOrder,
      metadata: sanitizeLessonSectionMetadata(payload.metadata),
      tags: payload.tags,
      status: payload.status,
    }),
    method: 'POST',
    path: '/lesson-sections/update',
    responseShape: {
      type: 'LessonSection',
    },
  },
  createQuizCaseOption: {
    buildData: (payload) => pickDefinedPayloadFields({
      groupName: payload.groupName,
      text: payload.text,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/quiz-case-options/create',
    responseShape: {
      type: 'QuizCaseOption',
    },
  },
  createQuizCase: {
    buildData: (payload) => pickDefinedPayloadFields({
      groupName: payload.groupName,
      title: payload.title,
      prompt: payload.prompt,
      optionPoolIds: payload.optionPoolIds,
      scoringRules: payload.scoringRules,
      difficultyRank: payload.difficultyRank,
      explanation: payload.explanation,
      optionSelectionMode: payload.optionSelectionMode,
      presentedOptionCount: payload.presentedOptionCount,
      randomizeOptionOrder: payload.randomizeOptionOrder,
      baseRewardPoints: payload.baseRewardPoints,
      rewardMultiplier: payload.rewardMultiplier,
      wrongAnswerPenaltyPoints: payload.wrongAnswerPenaltyPoints,
      useInGames: payload.useInGames,
      status: payload.status,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/quiz-cases/create',
    responseShape: {
      type: 'QuizCase',
    },
  },
  createQuizGame: {
    buildData: (payload) => pickDefinedPayloadFields({
      groupName: payload.groupName,
      title: payload.title,
      description: payload.description,
      casePoolIds: payload.casePoolIds,
      rules: payload.rules,
      caseSelectionMode: payload.caseSelectionMode,
      presentedCaseCount: payload.presentedCaseCount,
      randomizeCaseOrder: payload.randomizeCaseOrder,
      levelDistribution: payload.levelDistribution,
      status: payload.status,
      metadata: sanitizeMetadata(payload.metadata),
      tags: payload.tags,
    }),
    method: 'POST',
    path: '/quiz-games/create',
    responseShape: {
      type: 'QuizGame',
    },
  },
  activateQuizGame: {
    buildData: (payload) => pickDefinedPayloadFields({
      id: payload.id,
    }),
    method: 'POST',
    path: '/quiz-games/activate',
    responseShape: {
      type: 'QuizGame',
    },
  },
  deactivateQuizGame: {
    buildData: (payload) => pickDefinedPayloadFields({
      id: payload.id,
    }),
    method: 'POST',
    path: '/quiz-games/deactivate',
    responseShape: {
      type: 'QuizGame',
    },
  },
  updateQuizGame: {
    buildData: (payload = {}) => pickDefinedPayloadFields({
      id: payload.id,
      groupName: payload.groupName,
      title: payload.title,
      description: payload.description,
      casePoolIds: payload.casePoolIds,
      rules: payload.rules,
      caseSelectionMode: payload.caseSelectionMode,
      presentedCaseCount: payload.presentedCaseCount,
      randomizeCaseOrder: payload.randomizeCaseOrder,
      levelDistribution: payload.levelDistribution,
      metadata: sanitizeMetadata(payload.metadata),
      tags: payload.tags,
    }),
    method: 'POST',
    path: '/quiz-games/update',
    responseShape: {
      type: 'QuizGame',
    },
  },
  updateQuizCase: {
    buildData: (payload = {}) => pickDefinedPayloadFields({
      id: payload.id,
      groupName: payload.groupName,
      title: payload.title,
      prompt: payload.prompt,
      optionPoolIds: payload.optionPoolIds,
      scoringRules: payload.scoringRules,
      difficultyRank: payload.difficultyRank,
      explanation: payload.explanation,
      optionSelectionMode: payload.optionSelectionMode,
      presentedOptionCount: payload.presentedOptionCount,
      randomizeOptionOrder: payload.randomizeOptionOrder,
      baseRewardPoints: payload.baseRewardPoints,
      rewardMultiplier: payload.rewardMultiplier,
      wrongAnswerPenaltyPoints: payload.wrongAnswerPenaltyPoints,
      useInGames: payload.useInGames,
      status: payload.status,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/quiz-cases/update',
    responseShape: {
      type: 'QuizCase',
    },
  },
  updateQuizCaseOption: {
    buildData: (payload = {}) => pickDefinedPayloadFields({
      id: payload.id,
      groupName: payload.groupName,
      text: payload.text,
      status: payload.status,
      metadata: sanitizeMetadata(payload.metadata),
    }),
    method: 'POST',
    path: '/quiz-case-options/update',
    responseShape: {
      type: 'QuizCaseOption',
    },
  },
  getEducationAchievements: {
    path: '/achievements',
    responseShape: {
      item: 'EducationAchievement',
      type: 'array',
    },
  },
};

function createContractMethod(methodName) {
  const definition = endpointDefinitions[methodName];

  return {
    authContextShape: educationAuthContextShape,
    errorShape: educationApiErrorShape,
    method: 'GET',
    payloadShape: definition.payloadShape || contextPayloadShape,
    responseShape: definition.responseShape,
    ...definition,
  };
}

function normalizeBasePath(basePath = '/education') {
  const normalizedBasePath = basePath.startsWith('/') ? basePath : `/${basePath}`;

  return normalizedBasePath.replace(/\/$/, '');
}

function createContextPayload(context = {}) {
  return { context };
}

function pickDefinedPayloadFields(fields) {
  return Object.entries(fields).reduce((payload, [key, value]) => {
    if (value !== undefined) {
      payload[key] = value;
    }

    return payload;
  }, {});
}

const forbiddenMetadataKeys = new Set([
  'archivedAt',
  'createdAt',
  'raw',
  'status',
  'tags',
  'updatedAt',
]);

function sanitizeMetadata(metadata) {
  if (!isPlainObject(metadata)) return metadata;

  return sanitizeMetadataObject(metadata);
}

function sanitizeLessonSectionMetadata(metadata) {
  const safeMetadata = sanitizeMetadata(metadata);

  if (!isPlainObject(safeMetadata)) return safeMetadata;

  delete safeMetadata.tags;

  return safeMetadata;
}

function sanitizeMetadataObject(metadata, parentKey) {
  return Object.entries(metadata).reduce((safeMetadata, [key, value]) => {
    if (forbiddenMetadataKeys.has(key) || (parentKey === 'backend' && key === 'tags')) {
      return safeMetadata;
    }

    safeMetadata[key] = sanitizeMetadataValue(value, key);

    return safeMetadata;
  }, {});
}

function sanitizeMetadataValue(value, parentKey) {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeMetadataValue(item, parentKey));
  }

  if (isPlainObject(value)) {
    return sanitizeMetadataObject(value, parentKey);
  }

  return value;
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

const methodPayloadBuilders = {
  listTags: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  getLessonSections: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  listLessonSectionItems: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  getLessons: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  getQuizGames: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  getQuizGameContent: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  getQuizCaseOptions: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  getQuizCases: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  createLesson: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  updateLesson: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  publishLesson: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  listLessonMaterials: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  createLessonTextMaterial: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  updateLessonMaterial: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  createLessonLinkMaterial: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  createLessonFileMaterial: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  uploadLessonFileMaterial: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  uploadSmoozieFile: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  downloadLessonMaterialContent: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  createLessonDividerMaterial: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  detachLessonMaterial: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  setLessonMaterialsOrder: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  reorderLessonSectionItems: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  attachLessonSectionItem: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  updateLessonSectionItem: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  createLessonSection: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  updateLessonSection: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  getEducationKnowledgeItemDetails: (itemId, context = {}) => ({
    context,
    itemId,
  }),
  createQuizCaseOption: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  createQuizCase: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  createQuizGame: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  activateQuizGame: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  deactivateQuizGame: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  updateQuizGame: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  updateQuizCase: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
  updateQuizCaseOption: (context = {}, data = {}) => ({
    context,
    ...data,
  }),
};

export const educationApiContract = educationApiMethods.reduce((contract, methodName) => ({
  ...contract,
  [methodName]: createContractMethod(methodName),
}), {});

export function getEducationApiContractMethod(methodName) {
  const contractMethod = educationApiContract[methodName];

  if (!contractMethod) {
    throw new Error(`Unknown education api method: ${methodName}`);
  }

  return contractMethod;
}

export function createEducationMethodPayload(methodName, args = []) {
  const buildPayload = methodPayloadBuilders[methodName];

  getEducationApiContractMethod(methodName);

  return buildPayload ? buildPayload(...args) : createContextPayload(args[0]);
}

export function createEducationEndpointRequest(methodName, args = [], { basePath = '/education' } = {}) {
  const contractMethod = getEducationApiContractMethod(methodName);
  const payload = createEducationMethodPayload(methodName, args);
  const endpointPath = contractMethod.buildPath
    ? contractMethod.buildPath(payload)
    : contractMethod.path;

  return {
    context: payload.context,
    data: contractMethod.buildData ? contractMethod.buildData(payload) : null,
    method: contractMethod.method,
    methodName,
    path: `${normalizeBasePath(basePath)}${endpointPath}`,
    responseShape: contractMethod.responseShape,
  };
}
